'use client';

import { DropdownMenu } from '@app/app/_components/Dropdown/DropdownMenu';
import { DropdownOption } from '@app/app/_components/Dropdown/DropdownOption';
import { DropdownArrowIcon } from '@app/app/_icons/DropdownArrowIcon';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

interface Props {
    options: DropdownOption[];
    initialValue?: string;
    placeholder?: string;
    align?: 'right' | 'left';
    onChange?: (value: string) => void;
    id?: string;
    'aria-label'?: string;
}

export function Dropdown({
    options,
    initialValue,
    placeholder,
    align = 'right',
    onChange,
    id,
    'aria-label': ariaLabel,
}: Props) {
    const dropdownRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    const [open, setOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(
        options.find(option => option.value != null && option.value === initialValue),
    );

    const handleToggle = useCallback(() => {
        setOpen(isOpen => !isOpen);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleEsc = useCallback(() => {
        setOpen(false);

        dropdownRef.current?.focus();
    }, []);

    const handleOptionClick = useCallback(
        (e: DropdownOption) => {
            if (e.value != null) {
                setSelectedOption(e);

                onChange?.(e.value);
            }

            if (e.action != null) {
                e.action();
            }

            setOpen(false);
        },
        [onChange],
    );

    useClickOutside(handleClose, [dropdownRef, menuRef]);

    return (
        <div className="relative">
            <button
                ref={dropdownRef}
                className={twJoin(
                    'inline-flex cursor-pointer items-center gap-2 text-sm font-medium',
                    open && 'text-primary-500',
                )}
                onClick={handleToggle}
                type="button"
                tabIndex={0}
                aria-label={ariaLabel}
                aria-haspopup={true}
                aria-controls={id}>
                <div>{selectedOption == null ? placeholder : selectedOption.label}</div>
                <DropdownArrowIcon className={twJoin('size-3.5 shrink-0', open && 'rotate-180')} />
            </button>

            {open && (
                <DropdownMenu
                    ref={menuRef}
                    options={options}
                    placeholder={placeholder}
                    selectedOption={selectedOption}
                    onOptionClick={handleOptionClick}
                    onEsc={handleEsc}
                    align={align}
                    id={id}
                />
            )}
        </div>
    );
}
